/**
 * Drift (Ken Burns) keyframe helpers — shared by the drift component modes
 * (`mosaic_drift`, `mosaic_right_drift`, `gallery_drift`).
 *
 * In a drift tile, the static rounded frame clips an inner wrapper that is
 * animated with `transform: scale(S) translate(Tx, Ty)` (a slow zoom + pan).
 * The image inside fills the wrapper via `object-cover`, so the wrapper box is
 * what must always cover the frame. If it ever fails to, the frame's
 * background/edge shows through and the image visibly "shrinks" inside its slot
 * (the eng-00030 report: when the tile zooms out it no longer fills the space).
 *
 * Coverage math (transform-origin is the default center, and the translate is a
 * percentage of the wrapper's own pre-scale box):
 *
 *   - Scaling by S overflows the frame by `(S - 1) / 2` on each side.
 *   - Because the translate sits to the right of the scale in the transform
 *     list, it is applied in the wrapper's local space and then magnified by S,
 *     so the effective shift in the frame's coordinates is `S * T`.
 *   - Full coverage on both axes therefore requires, for every keyframe:
 *
 *         |S * T| <= (S - 1) / 2     <=>     |T| <= (S - 1) / (2 * S)
 *
 * `buildDriftPresets` authors the desired motion as intent fractions and clamps
 * every translate to this safe maximum (minus a margin), guaranteeing the pan
 * stays fully covered for any chosen scale — the root-cause fix for the bug
 * where hand-tuned presets translated further than their (small) zoom allowed.
 */

// Headroom kept below the geometric maximum. The animation interpolates scale
// and translate linearly between the `from` and `to` keyframes, so the
// intermediate path must stay covered too; clamping both endpoints to 80% of
// the safe maximum leaves room for that interpolation to never expose an edge.
export const DRIFT_TRANSLATE_SAFETY = 0.8;

/**
 * Maximum |translate| (as a fraction of the tile, per axis) that still keeps a
 * tile scaled by `scale` fully covering its frame. Derived from
 * `|T| <= (S - 1) / (2 * S)`. Returns 0 for scales <= 1 (no overflow to pan
 * into, so any translate would expose an edge).
 *
 * @param {number} scale
 * @returns {number} safe translate fraction (>= 0)
 */
export function maxSafeDriftTranslate(scale) {
  const s = Number(scale);
  if (!Number.isFinite(s) || s <= 1) return 0;
  return (s - 1) / (2 * s);
}

/**
 * Clamp a desired translate fraction to the coverage-safe range for a scale,
 * applying the interpolation safety margin.
 */
function clampDriftTranslate(fraction, scale) {
  const value = Number(fraction);
  const limit = maxSafeDriftTranslate(scale) * DRIFT_TRANSLATE_SAFETY;
  if (!Number.isFinite(value)) return 0;
  return Math.max(-limit, Math.min(limit, value));
}

/**
 * Build a single `transform` string from a scale + (clamped) translate intent.
 * Scale is floored at 1 (a drift tile must never be smaller than its frame).
 */
export function driftKeyframe(scale, txFraction, tyFraction) {
  const s = Math.max(1, Number(scale) || 1);
  const tx = clampDriftTranslate(txFraction, s);
  const ty = clampDriftTranslate(tyFraction, s);
  const pct = (value) => `${(value * 100).toFixed(3)}%`;
  return `scale(${s}) translate(${pct(tx)}, ${pct(ty)})`;
}

/**
 * Convert an array of drift intents into `{ from, to }` keyframe presets whose
 * translates are guaranteed to keep the tile covering its frame.
 *
 * Each intent: `{ fromScale, fromTx, fromTy, toScale, toTx, toTy }` where the
 * translate values are fractions of the tile (e.g. `0.05` == 5%).
 *
 * @param {Array<Object>} intents
 * @returns {Array<{from: string, to: string}>}
 */
export function buildDriftPresets(intents) {
  return (intents || []).map((intent) => ({
    from: driftKeyframe(intent.fromScale, intent.fromTx, intent.fromTy),
    to: driftKeyframe(intent.toScale, intent.toTx, intent.toTy),
  }));
}
